import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import './BlogManager.css';

const CATEGORIES = ['Culture', 'Drops', 'Style', 'Tech', 'Community'];

const EMPTY = {
  title: '', slug: '', excerpt: '', body: '',
  cover_image: '', category: 'Culture', author: 'SA Dropped', published: false,
};

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function BlogManager() {
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState('list'); // 'list' | 'form'
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [imgFile,  setImgFile]  = useState(null);
  const [imgPrev,  setImgPrev]  = useState('');
  const [error,    setError]    = useState('');

  const fetchPosts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false });
    setPosts(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm(EMPTY);
    setEditing(null);
    setImgFile(null);
    setImgPrev('');
    setError('');
    setView('form');
  };

  const openEdit = (post) => {
    setForm({
      title:       post.title,
      slug:        post.slug,
      excerpt:     post.excerpt || '',
      body:        post.body || '',
      cover_image: post.cover_image || '',
      category:    post.category,
      author:      post.author,
      published:   post.published,
    });
    setEditing(post);
    setImgFile(null);
    setImgPrev(post.cover_image || '');
    setError('');
    setView('form');
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setImgPrev(URL.createObjectURL(file));
  };

  const uploadImage = async () => {
    const ext  = imgFile.name.split('.').pop();
    const path = `blog/${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('product-images')
      .upload(path, imgFile, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage.from('product-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let cover_image = form.cover_image;
      if (imgFile) cover_image = await uploadImage();

      const payload = { ...form, cover_image };

      let result;
      if (editing) {
        result = await supabase.from('blog_posts').update(payload).eq('id', editing.id);
      } else {
        result = await supabase.from('blog_posts').insert([payload]);
      }
      if (result.error) throw result.error;
      setView('list');
      fetchPosts();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (deleting !== id) { setDeleting(id); return; }
    await supabase.from('blog_posts').delete().eq('id', id);
    setDeleting(null);
    fetchPosts();
  };

  const togglePublish = async (post) => {
    await supabase
      .from('blog_posts')
      .update({ published: !post.published })
      .eq('id', post.id);
    fetchPosts();
  };

  /* ── FORM VIEW ── */
  if (view === 'form') {
    return (
      <div className="bm">
        <div className="bm__form-header">
          <button className="bm__back" onClick={() => setView('list')}>
            ← Back to Posts
          </button>
          <h1 className="bm__form-title">
            {editing ? 'Edit Post' : 'New Post'}
          </h1>
        </div>

        <form className="bm__form" onSubmit={handleSave}>
          {error && <p className="bm__error">{error}</p>}

          <div className="bm__form-grid">
            {/* Left col */}
            <div className="bm__col">
              <p className="bm__section-label">Post Details</p>

              <div className="bm__field">
                <label>Title *</label>
                <input
                  required
                  value={form.title}
                  onChange={e => {
                    set('title', e.target.value);
                    if (!editing) set('slug', slugify(e.target.value));
                  }}
                  placeholder="The Air Force 1 Story"
                />
              </div>

              <div className="bm__field">
                <label>Slug *</label>
                <input
                  required
                  value={form.slug}
                  onChange={e => set('slug', slugify(e.target.value))}
                  placeholder="the-air-force-1-story"
                />
                <span className="bm__hint">sadropped.co.za/blog/{form.slug || 'your-slug'}</span>
              </div>

              <div className="bm__row">
                <div className="bm__field">
                  <label>Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="bm__field">
                  <label>Author</label>
                  <input
                    value={form.author}
                    onChange={e => set('author', e.target.value)}
                    placeholder="SA Dropped"
                  />
                </div>
              </div>

              <div className="bm__field">
                <label>Excerpt</label>
                <textarea
                  rows={3}
                  value={form.excerpt}
                  onChange={e => set('excerpt', e.target.value)}
                  placeholder="A short summary shown on the blog grid..."
                />
              </div>

              <div className="bm__field">
                <label>Body *</label>
                <textarea
                  required
                  rows={16}
                  value={form.body}
                  onChange={e => set('body', e.target.value)}
                  placeholder={`Write your post here...\n\nUse ## for headings\nUse **bold** for bold text\n\nEach new line = new paragraph`}
                  className="bm__body-textarea"
                />
                <span className="bm__hint">Supports: ## Heading, **bold**</span>
              </div>

              <div className="bm__field bm__field--inline">
                <label>Published</label>
                <div
                  className={`bm__toggle${form.published ? ' bm__toggle--on' : ''}`}
                  onClick={() => set('published', !form.published)}
                >
                  <span className="bm__toggle-knob" />
                </div>
                <span className="bm__toggle-lbl">
                  {form.published ? 'Live on site' : 'Draft'}
                </span>
              </div>
            </div>

            {/* Right col — cover image */}
            <div className="bm__col">
              <p className="bm__section-label">Cover Image</p>

              <label className="bm__upload">
                <input type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
                {imgPrev ? (
                  <img src={imgPrev} alt="Cover preview" className="bm__upload-preview" />
                ) : (
                  <div className="bm__upload-empty">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                      stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <p>Click to upload cover image</p>
                    <span>PNG, JPG, WEBP</span>
                  </div>
                )}
              </label>

              {imgPrev && (
                <button
                  type="button"
                  className="bm__clear-img"
                  onClick={() => { setImgFile(null); setImgPrev(''); set('cover_image', ''); }}
                >
                  Remove image
                </button>
              )}

              <div className="bm__field" style={{ marginTop: 16 }}>
                <label>Or paste image URL</label>
                <input
                  value={form.cover_image}
                  onChange={e => { set('cover_image', e.target.value); setImgPrev(e.target.value); }}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bm__form-footer">
            <button type="button" className="bm__btn-cancel" onClick={() => setView('list')}>
              Cancel
            </button>
            <button type="submit" className="bm__btn-save" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Publish Post'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  /* ── LIST VIEW ── */
  return (
    <div className="bm">
      <div className="ad__header">
        <div>
          <h1 className="ad__title">Blog Posts</h1>
          <p className="ad__subtitle">{posts.length} posts total</p>
        </div>
        <button className="ad__add-btn" onClick={openCreate}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Post
        </button>
      </div>

      {loading ? (
        <div className="ad__loading">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="ad__empty">
          <p>No posts yet.</p>
          <button onClick={openCreate}>Write your first post</button>
        </div>
      ) : (
        <div className="ad__table-wrap">
          <table className="ad__table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Category</th>
                <th>Author</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id}>
                  <td>
                    <div className="ad__product-cell">
                      {post.cover_image && (
                        <img src={post.cover_image} alt={post.title} className="ad__thumb" />
                      )}
                      <div>
                        <p className="ad__product-name">{post.title}</p>
                        <p className="ad__product-color">/blog/{post.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="ad__cell-muted">{post.category}</td>
                  <td className="ad__cell-muted">{post.author}</td>
                  <td>
                    <button
                      className={`ad__stock-btn${post.published ? ' ad__stock-btn--in' : ' ad__stock-btn--out'}`}
                      onClick={() => togglePublish(post)}
                    >
                      {post.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="ad__cell-muted">
                    {new Date(post.created_at).toLocaleDateString('en-ZA')}
                  </td>
                  <td>
                    <div className="ad__actions">
                      <button
                        className="ad__action-btn ad__action-btn--edit"
                        onClick={() => openEdit(post)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Edit
                      </button>
                      <a
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ad__action-btn ad__action-btn--edit"
                        onClick={e => e.stopPropagation()}
                      >
                        View
                      </a>
                      <button
                        className={`ad__action-btn${deleting === post.id ? ' ad__action-btn--confirm' : ' ad__action-btn--delete'}`}
                        onClick={() => handleDelete(post.id)}
                      >
                        {deleting === post.id ? '⚠ Confirm' : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}